import { useRef } from 'react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Image, VideoCamera, CaretLeft, CaretRight } from '@phosphor-icons/react';

export const Timeline = ({ scenes, selectedScene, onSelectScene, totalDuration }) => {
  const scrollRef = useRef(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Calculate cumulative time for each scene
  let cumulativeTime = 0;

  return (
    <div className="h-32 bg-[#121212] border-t border-[#333333] flex-shrink-0">
      {/* Timeline Header */}
      <div className="h-8 px-4 flex items-center justify-between border-b border-[#262626]">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#A1A1AA] uppercase tracking-wider">Timeline</span>
          <span className="text-xs text-[#A1A1AA]">({scenes.length} scenes)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={scrollLeft}
            className="p-1 text-[#A1A1AA] hover:text-white hover:bg-[#262626] rounded transition-colors"
            title="Scroll left"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            onClick={scrollRight}
            className="p-1 text-[#A1A1AA] hover:text-white hover:bg-[#262626] rounded transition-colors"
            title="Scroll right"
          >
            <CaretRight size={18} weight="bold" />
          </button>
          <span className="text-xs text-[#A1A1AA] ml-2">Total: {formatTime(totalDuration)}</span>
        </div>
      </div>

      {/* Timeline Track */}
      <div className="h-24 relative">
        <div 
          ref={scrollRef}
          className="h-full px-4 py-2 flex items-center gap-1 overflow-x-auto scrollbar-thin"
          style={{ scrollBehavior: 'smooth' }}
        >
          {scenes.map((scene, index) => {
            const startTime = cumulativeTime;
            cumulativeTime += scene.duration;
            const width = Math.max(60, scene.duration * 8); // Min 60px, scale by duration

            return (
              <div
                key={scene.id}
                onClick={() => onSelectScene(scene)}
                className={`relative h-16 rounded cursor-pointer transition-all flex-shrink-0 hover:scale-105 ${
                  selectedScene?.id === scene.id 
                    ? 'bg-[#FF5D01]/20 border-2 border-[#FF5D01] ring-2 ring-[#FF5D01]/30' 
                    : 'bg-[#262626] border border-[#333333] hover:border-[#FF5D01]/50'
                }`}
                style={{ width: `${width}px` }}
                data-testid={`timeline-scene-${scene.id}`}
              >
                {/* Scene Thumbnail or Placeholder */}
                <div className="absolute inset-0 overflow-hidden rounded">
                  {scene.asset_url ? (
                    <img src={scene.asset_url} alt="" className="w-full h-full object-cover opacity-60" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {scene.media_type === 'video' ? (
                        <VideoCamera size={20} className="text-[#333333]" />
                      ) : (
                        <Image size={20} className="text-[#333333]" />
                      )}
                    </div>
                  )}
                </div>
                
                {/* Scene Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-white font-mono">#{scene.scene_number}</span>
                    <span className="text-[10px] text-[#A1A1AA]">{scene.duration}s</span>
                  </div>
                </div>

                {/* Status indicator */}
                {scene.status === 'approved' && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-[#10B981] rounded-full" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
